import { useTileBrush } from './TilemapEditor.js';
import { brushOption, useTerrain } from '../terrain/terrain-store.js';
import { useCallback, useEffect, useState } from 'react';
import { Panel } from 'react-resizable-panels';

import { getView, type EngineView } from '../../engine/prism.js';
import { useRevealAssets } from '../../state/assets.js';
import { useConsole } from '../../state/console.js';
import { filesOfKind, useProjectFiles } from '../../state/project-files.js';
import { useScene } from '../../state/scene.js';
import { useWorkspace } from '../../state/workspace.js';
import { Assets } from '../panels/Assets.js';
import { Console } from '../panels/Console.js';
import { IconButton, isTyping, MenuButton, SelectField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { Handle, SplitGroup } from '../ui/layout.js';
import { Viewport } from '../Viewport.js';
import { ViewportAssetDrop } from './AssetDrop.js';
import { Hierarchy } from './Hierarchy.js';
import { Inspector } from './Inspector.js';
import { SceneSchematic } from './SceneSchematic.js';
import { LayoutMenu, OverlaysMenu, SceneOverlays, ViewModeMenu } from './SceneOverlays.js';
import { ParticleEffectOverlay } from '../particles/ParticleEffectOverlay.js';
import { findShortcut, frameSelection } from './scene-shortcuts.js';
import { useSceneView } from '../../state/scene-view.js';

export function PanelHeader({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <header className="panel-head">
      <span className="panel-title">{title}</span>
      <span className="panel-head-spacer" />
      {children}
    </header>
  );
}

export function SaveIndicator({ state }: { state: 'saved' | 'pending' | 'saving' | 'error' }) {
  const label = { saved: 'Saved', pending: 'Unsaved', saving: 'Saving…', error: 'Save failed' }[state];
  return (
    <span className={`save-state is-${state}`} title={label}>
      <span className="save-dot" />
      {label}
    </span>
  );
}

function ScenePicker() {
  const entries = useProjectFiles((s) => s.entries);
  const path = useScene((s) => s.path);
  const open = useScene((s) => s.open);
  const scenes = filesOfKind(entries, 'scene');
  return (
    <MenuButton
      className="picker"
      title="Open scene"
      items={() => [
        { id: 'h', label: 'Scenes', heading: true },
        ...scenes.map((p) => ({ id: p, label: p, icon: 'scene' as const, run: () => void open(p) })),
        ...(scenes.length === 0 ? [{ id: 'none', label: 'No *.scene.json files', disabled: true }] : []),
      ]}
    >
      <Icon name="scene" size={13} />
      <span className="picker-label">{path ? path.replace(/^scenes\//, '').replace(/\.scene\.json$/, '') : 'Open scene…'}</span>
      <Icon name="chevronDown" size={12} />
    </MenuButton>
  );
}

function SceneToolbar() {
  const tool = useScene((s) => s.tool);
  const space = useScene((s) => s.space);
  const snap = useScene((s) => s.snap);
  const speed = useScene((s) => s.cameraSpeed);
  const saveState = useScene((s) => s.saveState);
  const canUndo = useScene((s) => s.past.length > 0);
  const canRedo = useScene((s) => s.future.length > 0);
  const pivot = useSceneView((v) => v.pivot);
  const s = useScene.getState();
  return (
    <div className="viewport-toolbar">
      <div className="tb-group">
        <ScenePicker />
      </div>
      <div className="tb-group segmented">
        <IconButton icon="cursor" title="Select" shortcut="Q" active={tool === 'select'} onClick={() => s.setTool('select')} />
        <IconButton icon="move" title="Move" shortcut="W" active={tool === 'move'} onClick={() => s.setTool('move')} />
        <IconButton icon="rotate" title="Rotate" shortcut="E" active={tool === 'rotate'} onClick={() => s.setTool('rotate')} />
        <IconButton icon="scale" title="Scale" shortcut="R" active={tool === 'scale'} onClick={() => s.setTool('scale')} />
      </div>
      <div className="tb-group segmented">
        <IconButton
          icon={space === 'world' ? 'globe' : 'local'}
          title={space === 'world' ? 'World space' : 'Local space'}
          shortcut="X"
          onClick={() => s.setSpace(space === 'world' ? 'local' : 'world')}
        />
        <IconButton
          icon={pivot === 'pivot' ? 'focus' : 'square'}
          title={pivot === 'pivot' ? 'Pivot: handle at the active object' : 'Center: handle at the selection centre'}
          shortcut="Z"
          onClick={() => useSceneView.getState().setPivot(pivot === 'pivot' ? 'center' : 'pivot')}
        />
        <IconButton icon="magnet" title="Snap" shortcut="Ctrl+G" active={snap} onClick={() => s.setSnap(!snap)} />
      </div>
      <div className="tb-group">
        <ViewModeMenu />
        <LayoutMenu />
        <OverlaysMenu />
      </div>
      <div className="tb-group tb-speed" title="Camera speed">
        <Icon name="camera" size={13} />
        <SelectField
          value={String(speed)}
          options={['0.25', '0.5', '1', '2', '4', '8'].map((v) => ({ value: v, label: `${v}×` }))}
          onChange={(v) => s.setCameraSpeed(Number(v))}
        />
      </div>
      <span className="tb-spacer" />
      <div className="tb-group segmented">
        <IconButton icon="undo" title="Undo" shortcut="Ctrl+Z" disabled={!canUndo} onClick={s.undo} />
        <IconButton icon="redo" title="Redo" shortcut="Ctrl+Y" disabled={!canRedo} onClick={s.redo} />
      </div>
      <IconButton icon="focus" title="Frame selected" shortcut="F" onClick={() => frameSelected()} />
      <SaveIndicator state={saveState} />
    </div>
  );
}

function frameSelected() {
  if (useScene.getState().selection.length) frameSelection();
  else getView('scene')?.setOption('frame', '');
}

function BottomTabs() {
  const [tab, setTab] = useState<'assets' | 'console'>('assets');
  useRevealAssets(() => setTab('assets'));
  const errors = useConsole((s) => s.entries.filter((e) => e.level === 'error').length);
  return (
    <div className="bottom-panel">
      <div className="subtabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'assets'} className={`subtab${tab === 'assets' ? ' is-active' : ''}`} onClick={() => setTab('assets')}>
          <Icon name="folder" size={13} /> Assets
        </button>
        <button type="button" role="tab" aria-selected={tab === 'console'} className={`subtab${tab === 'console' ? ' is-active' : ''}`} onClick={() => setTab('console')}>
          <Icon name="console" size={13} /> Console
          {errors > 0 && <span className="badge badge-error">{errors}</span>}
        </button>
      </div>
      <div className="bottom-body">{tab === 'assets' ? <Assets /> : <Console />}</div>
    </div>
  );
}

/** Unity-style scene editor: hierarchy | viewport + assets/console | inspector. */
export function SceneTab({ active }: { active: boolean }) {
  const doc = useScene((s) => s.doc);
  const path = useScene((s) => s.path);
  const error = useScene((s) => s.error);
  const loading = useScene((s) => s.loading);
  const selection = useScene((s) => s.selection);
  const entries = useProjectFiles((s) => s.entries);
  const projectId = useWorkspace((s) => s.projectId);

  const onReady = useCallback(async (view: EngineView) => {
    const s = useScene.getState();
    view.setTool(s.tool);
    view.setOption('space', s.space);
    view.setOption('snap', String(s.snap));
    view.setOption('cameraSpeed', String(s.cameraSpeed));
    useSceneView.getState().pushAll(view);
    if (s.path) await view.openScene(s.path);
    if (s.selection.length) view.command({ cmd: 'select', ids: s.selection });
    const brush = useTileBrush.getState();
    if (brush.painting) view.setOption('tileBrush', JSON.stringify({ layer: brush.layer, tile: brush.tile, size: brush.size }));
    if (useTerrain.getState().painting) view.setOption('terrainBrush', brushOption(useTerrain.getState()));
  }, []);

  // Viewport shortcuts while this tab is showing.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      const shortcut = findShortcut(e);
      if (!shortcut) return;
      if (shortcut.id === 'scene.frame') frameSelected();
      else shortcut.run();
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  const scenes = filesOfKind(entries, 'scene');

  return (
    <div className="tab-body tab-scene">
      <SplitGroup id="scene-h" orientation="horizontal" fallback={{ hierarchy: 17, center: 61, inspector: 22 }}>
        <Panel id="hierarchy" minSize={170} className="pane">
          <PanelHeader title="Hierarchy" />
          {doc ? <Hierarchy /> : <div className="pane-empty muted">{loading ? 'Loading…' : 'No scene open'}</div>}
        </Panel>
        <Handle orientation="vertical" />
        <Panel id="center" minSize={320} className="pane pane-center">
          <SplitGroup id="scene-v" orientation="vertical" fallback={{ view: 70, bottom: 30 }}>
            <Panel id="view" minSize={160} className="pane">
              <SceneToolbar />
              <Viewport
                mode="scene"
                onReady={onReady}
                fallback={
                  doc ? (
                    <SceneSchematic doc={doc} selection={selection} onSelect={(ids) => useScene.getState().select(ids)} />
                  ) : undefined
                }
              >
                {!doc && !loading && projectId && (
                  <div className="viewport-message">
                    {error ? (
                      <>
                        <Icon name="error" size={22} />
                        <strong>Could not open the scene</strong>
                        <span className="mono muted">{error}</span>
                      </>
                    ) : scenes.length ? (
                      <>
                        <Icon name="scene" size={22} />
                        <strong>Open a scene</strong>
                        <div className="scene-list">
                          {scenes.slice(0, 8).map((p) => (
                            <button key={p} type="button" className="ghost small" onClick={() => void useScene.getState().open(p)}>
                              {p}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <Icon name="scene" size={22} />
                        <strong>No scenes in this project</strong>
                        <span className="muted">Ask the AI to set up a Prism Engine Cloud project, or create scenes/main.scene.json.</span>
                      </>
                    )}
                  </div>
                )}
                {doc && error && (
                  <div className="viewport-error" role="alert">
                    <Icon name="warning" size={13} /> {error}
                  </div>
                )}
                {path && <span className="viewport-caption mono">{path}</span>}
                <ViewportAssetDrop />
                {doc && <SceneOverlays />}
                {doc && <ParticleEffectOverlay />}
              </Viewport>
            </Panel>
            <Handle orientation="horizontal" />
            <Panel id="bottom" minSize={90} collapsible className="pane">
              <BottomTabs />
            </Panel>
          </SplitGroup>
        </Panel>
        <Handle orientation="vertical" />
        <Panel id="inspector" minSize={240} className="pane">
          <PanelHeader title="Inspector" />
          <div className="inspector">
            <Inspector />
          </div>
        </Panel>
      </SplitGroup>
    </div>
  );
}
